import styled from '@emotion/styled'
import React from 'react'
import {
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { BenefitIcon } from './BenefitIcon'
import type { ApiBrandBenefit } from '@storefronts/common-types'
import { scaledFontSize } from '../../style/scrollingLayout'

export const BenefitsCarouselViewport = styled.div<{
    paddingTop?: string
    paddingBottom?: string | number
    marginBottom?: string | number
    minHeight?: number
}>(
    {
        display: 'grid',
        gridTemplateAreas: '"content"',
        justifyItems: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
    },
    (props) => {
        const styles: Record<string, string | number | undefined> = {
            minHeight: props.minHeight ?? 24,
        }
        if (props.paddingTop != null) styles.paddingTop = props.paddingTop
        if (props.paddingBottom != null)
            styles.paddingBottom = props.paddingBottom
        if (props.marginBottom != null) styles.marginBottom = props.marginBottom
        return styles
    },
)

export const BenefitSlide = styled.div<{
    isActive: boolean
    delay: number
    fontWeight?: number
    gap?: number
    /** When true, active slide gets solid background to prevent bleed-through of inactive slides (Secure Checkout only) */
    $fillBackgroundWhenActive?: boolean
}>(
    {
        gridArea: 'content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: scaledFontSize(13),
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        transition: 'opacity 0.4s ease-in-out',
        textAlign: 'center',
    },
    (props) => {
        const baseStyles = {
            gap: props.gap ?? 8,
            ...getTypographyRoleStyle('captionText', {
                fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
                fontWeight: props.fontWeight ?? 400,
            }),
            opacity: props.isActive ? 1 : 0,
            // Allow text to wrap when used without icon (Secure Checkout); minWidth: 0
            // lets flex child shrink so long text can wrap on narrow viewports
            minWidth: props.gap === 0 ? 0 : undefined,
        }

        if (props.$fillBackgroundWhenActive) {
            // Secure Checkout: prevent text bleed-through in Safari/FB in-app browser.
            // 1) visibility: hidden after fade-out removes inactive from paint entirely
            // 2) stretch + background covers any residual bleed
            // 3) z-index ensures active slide paints on top
            const visibilityTransition =
                'opacity 0.4s ease-in-out, visibility 0s 0.4s'
            return {
                ...baseStyles,
                visibility: props.isActive ? 'visible' : 'hidden',
                transition: visibilityTransition,
                transitionDelay: props.isActive
                    ? `${props.delay}s, 0s`
                    : '0s, 0.4s',
                justifySelf: 'stretch',
                alignSelf: 'stretch',
                position: 'relative',
                zIndex: props.isActive ? 1 : 0,
                ...(props.isActive
                    ? {
                          background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                      }
                    : {}),
            }
        }

        return {
            ...baseStyles,
            transition: 'opacity 0.4s ease-in-out',
            transitionDelay: `${props.delay}s`,
        }
    },
)

interface BenefitSlideContentProps {
    benefit: ApiBrandBenefit
    showIcon?: boolean
    iconSize?: number
}

export const BenefitSlideContent: React.FC<BenefitSlideContentProps> = ({
    benefit,
    showIcon = true,
    iconSize,
}) => (
    <>
        {showIcon && (
            <BenefitIcon
                type={benefit.type}
                {...(iconSize != null
                    ? { width: iconSize, height: iconSize }
                    : {})}
                strokeWidth={1.5}
            />
        )}
        {benefit.description}
    </>
)
